import {
  ActionButton,
  ComboBox,
  DefaultButton,
  IComboBoxOption,
  PrimaryButton,
  Stack,
  Text,
} from "@fluentui/react";
import { DragDropList, SignalServiceBase, css, useTheme } from "cayo.ui";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import logger from "@cayo/shared/libs/logger";
import { FCC } from "@cayo/shared/types/react";
import { Column, Row, RowPadding, ScrollableColumn } from "@cayo/shared/components/CommonLayout";
import ObjectIcon from "../ObjectIcon";
import Panel from "../Panel/Panel";
import { commonMessages } from "@cayo/shared/messages/common";
import {
  ChooseColumnsDialogController,
  ColumnData,
  IChooseColumnsDialogState,
  IChooseColumnsDialogStateItem,
} from "./ChooseColumnsDialog.controller";

const log = logger.getLogger("QUICK-FILTER-NEW ChooseColumnsDialog");

type ChooseColumnDialogProps = {
  userColumns: ColumnData[];
  allColums: ColumnData[];
  onClose: () => void;
  onSave: (columns: string[]) => void;
};

const dragDropListColumns = [{ key: "key", minWidth: 100, name: "key" }];

const initialState: IChooseColumnsDialogState = {
  items: [],
  canAdd: false,
  canSave: false,
};

export function ChooseColumnsDialog(props: ChooseColumnDialogProps): JSX.Element {
  // TODO: refactoring. create an instance of the controller only once
  const ctrl = useRef(
    new ChooseColumnsDialogController(
      initialState,
      props.allColums,
      props.userColumns,
      new SignalServiceBase()
    )
  ).current;

  const [dialogState, setDialogState] = useState(ctrl.state);

  const intl = useIntl();
  const { cayoTheme } = useTheme();

  useEffect(function mount() {
    const unsubscribe = ctrl.subscribe((state) => {
      log.debug("state changed -> state", state);
      setDialogState(state!);
    });

    return () => {
      unsubscribe();
      ctrl.dispose();
    };
  }, []);

  const addColumn = useCallback(() => {
    ctrl.addNew();
  }, []);

  const onRenderItem = (v: IChooseColumnsDialogStateItem) => {
    //log.debug("onRenderItem -> i, v", i, v);
    return (
      <ColumnItem
        key={v.key}
        options={v.value}
        remove={() => ctrl.remove(v.key)}
        canRemove={ctrl.canRemove}
        change={(value) => {
          log.debug("change -> key, value", v.key, value);
          ctrl.change(v.key, value);
        }}
      />
    );
  };

  const reorder = (v: IChooseColumnsDialogStateItem[]) => {
    ctrl.reorder(v.map((i) => i.key));
  };

  return (
    <Panel type={"smallFluid"} isBlocking={true} onClose={props.onClose} isOpen={true}>
      <ScrollableColumn>
        {/* header TODO: icon  */}
        <div>
          <RowPadding>
            <ObjectIcon iconName="CheckboxComposite" />

            <Column>
              <Text block variant="large" styles={{ root: { marginLeft: 20, marginTop: 10 } }}>
                <FormattedMessage
                  id="choose-columns-dialog.title"
                  defaultMessage="Choose columns"
                />
              </Text>
            </Column>
          </RowPadding>
        </div>
        <Stack
          horizontal={false}
          verticalFill={true}
          tokens={cayoTheme.metrics.stackTokens}
          styles={{
            root: {
              padding: "20px 24px 8px 24px",
              flexGrow: 1,
              position: "relative",
              overflowY: "auto",
              height: "100%",
              display: "flex",
              overflowX: "hidden",
            },
          }}
        >
          <DragDropList
            columns={dragDropListColumns}
            items={dialogState.items}
            setValue={reorder}
            onRenderItem={onRenderItem}
          />
          <ActionButton
            iconProps={{ iconName: "Add" }}
            onClick={addColumn}
            disabled={!dialogState.canAdd}
            styles={{ root: { alignSelf: "flex-start" } }}
          >
            {intl.formatMessage(commonMessages.addColumn)}
          </ActionButton>
        </Stack>
        <Stack
          horizontal={true}
          horizontalAlign={"end"}
          styles={{ root: { padding: "20px" } }}
          tokens={cayoTheme.metrics.stackTokens}
        >
          <PrimaryButton
            disabled={!dialogState.canSave}
            onClick={() => {
              props.onSave(ctrl.selectedColumns);
            }}
          >
            {intl.formatMessage(commonMessages.save)}
          </PrimaryButton>

          <DefaultButton
            disabled={false}
            onClick={() => {
              props.onClose();
            }}
          >
            {intl.formatMessage(commonMessages.cancel)}
          </DefaultButton>
        </Stack>
      </ScrollableColumn>
    </Panel>
  );
}

type ColumnItemProps = {
  options: IComboBoxOption[];
  canRemove: boolean;
  remove: () => void;
  change: (v: string) => void;
};

const ColumnItem: FCC<ColumnItemProps> = (props) => {
  return (
    <Row className={`${css.pb0} ${css.pointer}`} valign draggable>
      <span className={css.px4}>&nbsp;::&nbsp;</span>
      <ComboBox
        key={Date.now()}
        options={props.options}
        onChange={(e, option, i, v) => {
          log.debug("ComboBox.changed -> option, i, v", option, i, v);
          props.change(String(option?.key));
        }}
        styles={{ container: { flexGrow: 1 } }}
      />
      <ActionButton
        styles={{ root: { paddingBottom: 4 } }}
        disabled={!!props.canRemove}
        iconProps={{ iconName: "Cancel" }}
        onClick={props.remove}
      />
    </Row>
  );
};
